import { useCallback, useState } from 'react';
import { Privilege, RolePrivilege } from '../../../shared/RoleBasedTypes';
import { useAuth } from '../../../hooks/useAuth';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import {
	addOrUpdateRolePrivilegeMapping,
	getModulePrivilegeMappings,
	getRolePrivilegeMappings,
} from '../../../service/RoleBasedService';
import { displayError } from '../../../shared/Utils';
import useUserSession from '../../../hooks/useUserSession';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

export const useManagePrivileges = () => {
	const [privilegesByRole, setPrivilegesByRole] = useState<
		Array<RolePrivilege>
	>([]);
	const [privilegesByModule, setPrivilegesByModule] = useState<
		Array<Privilege>
	>([]);
	const [selectedModuleCode, setSelectedModuleCode] = useState<string>('');
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();

	const fetchPrivilegesByModule = useCallback(
		async (moduleId: number) => {
			setLoading(true);
			try {
				const privilegesResponse = await getModulePrivilegeMappings({
					modulePrivilegeFilters: {
						moduleId,
						status: ['ACTIVE', 'INACTIVE'],
					},
					pageIndex: 0,
					pageSize: 200,
				});

				if (Array.isArray(privilegesResponse?.modules?.[0]?.privileges)) {
					setPrivilegesByModule(privilegesResponse.modules[0].privileges);
					const module = privilegesResponse.modules[0].moduleCode;
					setSelectedModuleCode(module || '');
				} else {
					setPrivilegesByModule([]);
					setSelectedModuleCode('');
				}
			} catch (e) {
				const message = displayError(e);
				showToastMessage('error', message);
			}
			setLoading(false);
		},
		[setLoading, showToastMessage]
	);

	const fetchPrivilegesByRole = useCallback(
		async (roleCode: string, moduleId: number) => {
			setLoading(true);
			try {
				const privilegesResponse = await getRolePrivilegeMappings({
					roleCode,
					moduleId,
					filters: {
						status: ['ACTIVE', 'INACTIVE'],
					},
				});

				if (
					privilegesResponse?.privileges &&
					Array.isArray(privilegesResponse.privileges)
				) {
					setPrivilegesByRole(privilegesResponse.privileges);
				} else {
					setPrivilegesByRole([]);
				}
			} catch (e) {
				const message = displayError(e);
				showToastMessage('error', message);
			}
			setLoading(false);
		},
		[setLoading, showToastMessage]
	);

	const updatePrivileges = useCallback(
		async (roleId: number, moduleId: number, privilegeIds: number[]) => {
			if (srcmId) {
				setLoading(true);
				try {
					await addOrUpdateRolePrivilegeMapping({
						srcmId,
						selectedRoleCode: selectedRole as UserRole,
						moduleId,
						privilegeIds,
						roleId,
					});
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				}
				setLoading(false);
			}
		},
		[srcmId, setLoading, selectedRole, showToastMessage]
	);

	return {
		fetchPrivilegesByRole,
		fetchPrivilegesByModule,
		privilegesByRole,
		privilegesByModule,
		updatePrivileges,
		selectedModuleCode,
	};
};
